import urlJoin from "url-join";

export interface ApiResponse<T> {
	data: T;
	status: number;
	statusText: string;
	headers: Headers;
}

export class ApiError extends Error {
	response?: {
		status: number;
		statusText: string;
		data: unknown;
	};
	config?: {
		url: string;
		method: string;
		headers: Record<string, string>;
	};
	request?: boolean;

	constructor(
		message: string,
		options?: {
			response?: ApiError["response"];
			config?: ApiError["config"];
			request?: boolean;
		},
	) {
		super(message);
		this.name = "ApiError";
		this.response = options?.response;
		this.config = options?.config;
		this.request = options?.request;
	}
}

export interface RequestConfig {
	params?: Record<string, unknown> | URLSearchParams;
	headers?: Record<string, string>;
	timeout?: number;
	onUploadProgress?: (event: { loaded: number; total: number }) => void;
}

export interface ApiClient {
	defaults: {
		headers: {
			common: Record<string, string>;
		};
	};
	get: <T = unknown>(
		url: string,
		config?: RequestConfig,
	) => Promise<ApiResponse<T>>;
	post: <_TReq = unknown, TRes = ApiResponse<unknown>>(
		url: string,
		data?: unknown,
		config?: RequestConfig,
	) => Promise<TRes>;
	put: <_TReq = unknown, TRes = ApiResponse<unknown>>(
		url: string,
		data?: unknown,
		config?: RequestConfig,
	) => Promise<TRes>;
	patch: <_TReq = unknown, TRes = ApiResponse<unknown>>(
		url: string,
		data?: unknown,
		config?: RequestConfig,
	) => Promise<TRes>;
	delete: <_TReq = unknown, TRes = ApiResponse<unknown>>(
		url: string,
		config?: RequestConfig,
	) => Promise<TRes>;
}

interface ClientOptions {
	timeout?: number;
	defaultHeaders?: Record<string, string>;
	onError?: (error: ApiError) => void;
}

/**
 * Replicates axios's hybrid param serialization:
 * - Flat arrays (primitives) use brackets format: key[]=a&key[]=b
 * - Arrays of objects use indices format: key[0][prop]=a&key[1][prop]=b
 *
 * This matters because qs.parse on the server uses a default arrayLimit of 20.
 * A single arrayFormat can't handle both cases correctly:
 * - "brackets" breaks arrays of objects (loses element boundaries)
 * - "indices" breaks flat arrays >20 items (parsed as objects due to arrayLimit)
 */
export function serializeParams(params: Record<string, unknown>): string {
	const parts: string[] = [];

	function isVisitable(value: unknown): value is Record<string, unknown> | unknown[] {
		return (
			(typeof value === "object" && value !== null && !(value instanceof Date)) ||
			Array.isArray(value)
		);
	}

	function isFlatArray(arr: unknown[]): boolean {
		return arr.every((item) => !isVisitable(item));
	}

	function encode(val: string): string {
		return encodeURIComponent(val);
	}

	function build(prefix: string, value: unknown): void {
		if (value == null) return;

		if (Array.isArray(value)) {
			if (isFlatArray(value)) {
				// Flat arrays: brackets without indices (like axios)
				for (const item of value) {
					if (item != null) {
						parts.push(`${encode(prefix)}[]=${encode(String(item))}`);
					}
				}
			} else {
				// Arrays of objects: use numeric indices (like axios)
				value.forEach((item, index) => {
					if (item != null) {
						build(`${prefix}[${index}]`, item);
					}
				});
			}
		} else if (typeof value === "object" && value !== null && !(value instanceof Date)) {
			for (const [key, val] of Object.entries(value)) {
				build(`${prefix}[${key}]`, val);
			}
		} else {
			parts.push(`${encode(prefix)}=${encode(String(value))}`);
		}
	}

	for (const [key, value] of Object.entries(params)) {
		if (value == null) continue;

		if (Array.isArray(value)) {
			build(key, value);
		} else if (typeof value === "object" && value !== null && !(value instanceof Date)) {
			for (const [subKey, subVal] of Object.entries(value)) {
				build(`${key}[${subKey}]`, subVal);
			}
		} else {
			parts.push(`${encode(key)}=${encode(String(value))}`);
		}
	}

	return parts.join("&");
}

function buildUrl(
	baseURL: string,
	url: string,
	params?: Record<string, unknown> | URLSearchParams,
): string {
	const fullUrl = url.startsWith("http") ? url : urlJoin(baseURL, url);
	if (!params) return fullUrl;

	const queryString =
		params instanceof URLSearchParams
			? params.toString()
			: serializeParams(params);

	if (!queryString) return fullUrl;

	// Handle URLs that already contain a query string
	const separator = fullUrl.includes("?") ? "&" : "?";
	return `${fullUrl}${separator}${queryString}`;
}

function createTimeoutSignal(timeout: number): {
	signal: AbortSignal | undefined;
	clear: () => void;
} {
	if (timeout <= 0) return { signal: undefined, clear: () => {} }; // 0 means no timeout (match axios behavior)
	const controller = new AbortController();
	const id = setTimeout(() => controller.abort(), timeout);
	return { signal: controller.signal, clear: () => clearTimeout(id) };
}

function uploadWithXHR<T>(
	fullUrl: string,
	method: string,
	data: unknown,
	headers: Record<string, string>,
	timeout: number,
	onUploadProgress: (event: { loaded: number; total: number }) => void,
): Promise<ApiResponse<T>> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open(method.toUpperCase(), fullUrl);
		// 0 means no timeout in XHR (same as axios)
		xhr.timeout = timeout;

		for (const [key, value] of Object.entries(headers)) {
			xhr.setRequestHeader(key, value);
		}

		xhr.upload.addEventListener("progress", (event) => {
			onUploadProgress({ loaded: event.loaded, total: event.total });
		});

		xhr.addEventListener("load", () => {
			let responseData: T;
			try {
				responseData = JSON.parse(xhr.responseText);
			} catch {
				responseData = xhr.responseText as unknown as T;
			}

			const response: ApiResponse<T> = {
				data: responseData,
				status: xhr.status,
				statusText: xhr.statusText,
				headers: new Headers(),
			};

			if (xhr.status >= 200 && xhr.status < 300) {
				resolve(response);
			} else {
				reject(
					new ApiError(`Request failed with status ${xhr.status}`, {
						response: {
							status: xhr.status,
							statusText: xhr.statusText,
							data: responseData,
						},
						config: { url: fullUrl, method, headers },
					}),
				);
			}
		});

		xhr.addEventListener("error", () => {
			reject(
				new ApiError("Network error", {
					config: { url: fullUrl, method, headers },
					request: true,
				}),
			);
		});

		xhr.addEventListener("timeout", () => {
			reject(
				new ApiError(`Request timeout of ${timeout}ms exceeded`, {
					config: { url: fullUrl, method, headers },
					request: true,
				}),
			);
		});

		xhr.send(data as XMLHttpRequestBodyInit);
	});
}

export function createApiClient(
	baseURL: string,
	options: ClientOptions = {},
): ApiClient {
	const {
		timeout: defaultTimeout = 120_000,
		defaultHeaders = {},
		onError,
	} = options;

	const defaults = {
		headers: {
			common: {} as Record<string, string>,
		},
	};

	const request = async <T>(
		method: string,
		url: string,
		data?: unknown,
		config?: RequestConfig,
	): Promise<ApiResponse<T>> => {
		const timeout = config?.timeout ?? defaultTimeout;
		const fullUrl = buildUrl(baseURL, url, config?.params);
		const headers: Record<string, string> = {
			...defaultHeaders,
			...defaults.headers.common,
			...config?.headers,
		};

		// For upload progress, use XHR
		if (config?.onUploadProgress) {
			// Don't set Content-Type for FormData — let XHR/browser set boundary
			if (!(data instanceof FormData)) {
				headers["Content-Type"] = "application/json";
			}
			try {
				return await uploadWithXHR<T>(
					fullUrl,
					method,
					data instanceof FormData ? data : JSON.stringify(data),
					headers,
					timeout,
					config.onUploadProgress,
				);
			} catch (error) {
				if (error instanceof ApiError && onError) {
					onError(error);
				}
				throw error;
			}
		}

		// Don't set Content-Type for FormData — let browser set multipart boundary
		if (data !== undefined && !(data instanceof FormData)) {
			headers["Content-Type"] = "application/json";
		}

		const { signal, clear: clearTimeout } = createTimeoutSignal(timeout);
		const fetchOptions: RequestInit = {
			method: method.toUpperCase(),
			headers,
			signal,
		};

		if (data !== undefined) {
			fetchOptions.body =
				data instanceof FormData ? data : JSON.stringify(data);
		}

		let response: Response;
		try {
			response = await fetch(fullUrl, fetchOptions);
		} catch (error) {
			clearTimeout();
			const apiError = new ApiError(
				error instanceof Error ? error.message : "Network error",
				{
					config: { url: fullUrl, method, headers },
					request: true,
				},
			);
			onError?.(apiError);
			throw apiError;
		}

		clearTimeout();

		// Handle 204 No Content — no body to parse
		if (response.status === 204) {
			return {
				data: undefined as T,
				status: response.status,
				statusText: response.statusText,
				headers: response.headers,
			};
		}

		let responseData: T;
		const contentType = response.headers.get("content-type");
		if (contentType?.includes("application/json")) {
			responseData = await response.json();
		} else {
			const text = await response.text();
			try {
				responseData = JSON.parse(text);
			} catch {
				responseData = text as unknown as T;
			}
		}

		if (!response.ok) {
			const apiError = new ApiError(
				`Request failed with status ${response.status}`,
				{
					response: {
						status: response.status,
						statusText: response.statusText,
						data: responseData,
					},
					config: { url: fullUrl, method, headers },
				},
			);
			onError?.(apiError);
			throw apiError;
		}

		return {
			data: responseData,
			status: response.status,
			statusText: response.statusText,
			headers: response.headers,
		};
	};

	// Arrow functions so methods can be extracted without losing context
	// (critical for Redux sagas: yield call(api.get, url))
	return {
		defaults,

		get: <T = unknown>(url: string, config?: RequestConfig) =>
			request<T>("GET", url, undefined, config),

		post: <_TReq = unknown, TRes = ApiResponse<unknown>>(
			url: string,
			data?: unknown,
			config?: RequestConfig,
		) => request("POST", url, data, config) as Promise<TRes>,

		put: <_TReq = unknown, TRes = ApiResponse<unknown>>(
			url: string,
			data?: unknown,
			config?: RequestConfig,
		) => request("PUT", url, data, config) as Promise<TRes>,

		patch: <_TReq = unknown, TRes = ApiResponse<unknown>>(
			url: string,
			data?: unknown,
			config?: RequestConfig,
		) => request("PATCH", url, data, config) as Promise<TRes>,

		delete: <_TReq = unknown, TRes = ApiResponse<unknown>>(
			url: string,
			config?: RequestConfig,
		) => request("DELETE", url, undefined, config) as Promise<TRes>,
	};
}
